import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Language } from '../../types_i18n';
import './SituationScreen.css';

const GOALS: Array<{
  id: 'first-time' | 'failed' | 'exam-soon';
  titleKey: string;
  descKey: string;
  icon: string;
  iconBg: string;
}> = [
  {
    id: 'first-time',
    titleKey: 'first_time_title',
    descKey: 'first_time_desc',
    icon: '📚',
    iconBg: '#1E40AF',
  },
  {
    id: 'failed',
    titleKey: 'failed_title',
    descKey: 'failed_desc',
    icon: '⚠️',
    iconBg: '#991B1B',
  },
  {
    id: 'exam-soon',
    titleKey: 'exam_soon_title',
    descKey: 'exam_soon_desc',
    icon: '⏱️',
    iconBg: '#15803D',
  },
];

const TEXTS: Record<Language, Record<string, string>> = {
  nl: {
    title: 'Wat is je situatie?',
    subtitle: 'Zodat we weten wat je het meest nodig hebt — en je verspilde tijd besparen.',
    first_time_title: 'Ik doe het voor het eerst',
    first_time_desc: 'Ik wil het in één keer halen, zonder te gokken',
    failed_title: 'Ik ben eerder gezakt',
    failed_desc: 'Ik wil precies weten waar het misging en het oplossen',
    exam_soon_title: 'Mijn examen is snel',
    exam_soon_desc: 'Binnen 4 weken — elke studiesessie moet tellen',
    next_button: 'Verder',
  },
  en: {
    title: 'What is your situation?',
    subtitle: 'So we know what you need most — and spare you wasted time.',
    first_time_title: 'Taking it for the first time',
    first_time_desc: 'I want to pass on the first try',
    failed_title: 'I\'ve failed before',
    failed_desc: 'I want to know exactly where things went wrong and how...',
    exam_soon_title: 'My exam is soon',
    exam_soon_desc: 'In 4 weeks — every study session counts',
    next_button: 'Next',
  },
  ar: {
    title: 'ما وضعك؟',
    subtitle: 'لكي نعرف ما تحتاجه أكثر — وننقذك من إضاعة الوقت.',
    first_time_title: 'أول مرة لي',
    first_time_desc: 'أريد النجاح من المحاولة الأولى',
    failed_title: 'لقد فشلت من قبل',
    failed_desc: 'أريد أن أعرف بالضبط أين حدث الخطأ وكيف...',
    exam_soon_title: 'امتحاني قريب',
    exam_soon_desc: 'في غضون 4 أسابيع — كل جلسة دراسة مهمة',
    next_button: 'التالي',
  },
};

export default function SituationScreen() {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [selectedGoal, setSelectedGoal] = useState<'first-time' | 'failed' | 'exam-soon' | null>(null);

  const lang = (currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en') as Language;
  const t = TEXTS[lang];

  const handleSelect = (goalId: 'first-time' | 'failed' | 'exam-soon') => {
    setSelectedGoal(goalId);
    try {
      localStorage.setItem('selectedGoal', goalId);
    } catch {
      // ignore
    }
    setTimeout(() => navigate('/exam-date'), 240);
  };

  return (
    <div className="situation-screen">
      <button className="onboarding-back-btn" onClick={() => navigate(-1)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="20" height="20">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>

      <div className="onboarding-step-dots">
        <div className="onboarding-step-dot done" />
        <div className="onboarding-step-dot active" />
        <div className="onboarding-step-dot" />
      </div>

      <div className="onboarding-content">
        <h1 className="onboarding-title">{t.title}</h1>
        <p className="onboarding-subtitle">{t.subtitle}</p>

        <div className="situation-options">
          {GOALS.map(({ id, titleKey, iconBg }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleSelect(id)}
              className={`goal-option${selectedGoal === id ? ' selected' : ''}`}
            >
              <span className="goal-icon-container" style={{ background: iconBg }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {id === 'first-time' && <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H12m0 0h5.5A2.5 2.5 0 0 1 20 19.5M12 17v6m0 0H9m3 0h3"/>}
                  {id === 'failed' && <>
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 8v4m0 4h.01"/>
                  </>}
                  {id === 'exam-soon' && <>
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </>}
                </svg>
              </span>
              <span className="goal-info">
                <span className="goal-name">{t[titleKey as keyof typeof t]}</span>
              </span>
              <span className="goal-checkmark">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" width="12" height="12">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
